import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { currentEditorType } from 'packages/store/creativeEditor/creativeEditor.selectors';
import useEditor from './useEditor';
import useDesign from 'packages/pages/design/hooks/useDesign';

const defaultValidation = {
    isValid: true,
    isChildrenValid: true,
    validationMessages: [],
};

export default function useElementValidation(elementId: number) {
    const editorType = useSelector(currentEditorType);
    const designContext = useDesign();
    const editorContext = useEditor();
    const { creativeModel, validationManager } = editorType === 'create' ? designContext : editorContext;
    const [validationState, setValidationState] = useState(
        () => (elementId && validationManager?.getElementValidationState(elementId)) || defaultValidation,
    );
    const elId = useRef(elementId);

    useEffect(() => {
        if (!validationManager || !creativeModel) {
            return;
        }

        if (elId.current !== elementId) {
            if (elementId) {
                setValidationState(validationManager.getElementValidationState(elementId) || defaultValidation);
                elId.current = elementId;
            } else {
                setValidationState(defaultValidation);

                return;
            }
        }

        const validationChangeListener = ({ isValid, isChildrenValid, validationMessages }) => {
            setValidationState({ isValid, isChildrenValid, validationMessages });
        };
        const changesListener = () => {
            if (!elementId) {
                return;
            }

            const newState = validationManager.getElementValidationState(elementId) || defaultValidation;
            setValidationState((prev) => {
                if (
                    prev.isValid === newState.isValid &&
                    prev.isChildrenValid === newState.isChildrenValid &&
                    prev.validationMessages.length === newState.validationMessages.length
                ) {
                    return prev;
                }

                return newState;
            });
        };

        const unsubscribeElementValidationChange = validationManager.onElementValidationChange(
            elementId,
            validationChangeListener,
        );
        const unsubscribeElementUpdated = creativeModel.onElementUpdate(elementId, changesListener);
        creativeModel.getEventEmitter().on('currentPageChange', changesListener);
        creativeModel.getEventEmitter().on('pageOrderChange', changesListener);

        return () => {
            unsubscribeElementValidationChange();
            unsubscribeElementUpdated();
            creativeModel.getEventEmitter().off('currentPageChange', changesListener);
            creativeModel.getEventEmitter().off('pageOrderChange', changesListener);
        };
    }, [elementId, creativeModel, validationManager]);

    return validationState;
}
